import { IconProps } from '@euler/app/components/icons/types';
import { Input } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useServiceAgentList } from '@euler/functions/useServiceAgentList';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { memo } from 'react';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { useOrderFormStyles } from '../components/styles';

export const ServiceAgentRow = memo(
  ({
    required = true,
    ...props
  }: {
    required?: boolean;
    disabled?: boolean;
  }) => {
    const {
      theme,
      navigation,
      formController: { form },
    } = useOrderFlowContext();

    const { iconColor, placeholderColor } = theme;
    const { showActionSheetWithOptions } = useActionSheet();
    const serviceAgents = useServiceAgentList(null);
    const styles = useOrderFormStyles();

    const onPress = usePersistFn(() => {
      if (null == serviceAgents) return;
      if (serviceAgents.length > 4) {
        navigation.push('_serviceAgentSelector', {
          selectedServiceAgentId: form.serviceAgentInfo.value?.serviceAgentId,
          onSelect: sa => {
            form.serviceAgentInfo.update({
              serviceAgentId: sa.id,
              name: sa.name,
            });
          },
        });
        return;
      }
      showActionSheetWithOptions(
        {
          title: '选择服务顾问',
          message: !serviceAgents.length
            ? '您所在的门店尚未添加拥有服务顾问角色的用户'
            : undefined,
          options: [...serviceAgents.map(x => x.name), '关闭'],
          cancelButtonIndex: serviceAgents.length,
        },
        async buttonIndex => {
          if (buttonIndex == null || buttonIndex === serviceAgents.length) {
            return;
          }
          const sa = serviceAgents[buttonIndex];
          form.serviceAgentInfo.update({
            serviceAgentId: sa.id,
            name: sa.name,
          });
        },
      );
    });

    return (
      <Row
        errorProp="serviceAgentInfo"
        detailIcon={'disclosure'}
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        onPress={onPress}
        {...props}
      >
        <Cell label="服务顾问" required={required}>
          <Input
            style={styles.formInput}
            placeholderTextColor={placeholderColor}
            value={form.serviceAgentInfo.value?.name ?? ''}
            placeholder="请选择服务顾问"
            errorMsg={form.serviceAgentInfo.error}
            editable={false}
            loading={serviceAgents == null}
            pointerEvents="none"
          />
        </Cell>
      </Row>
    );
  },
);

const RowIcon = memo(({ size, color, style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      width={size ?? rpx(18)}
      height={size ?? rpx(21)}
      fill="none"
      viewBox="0 0 18 21"
      style={style}
      {...props}
    >
      <Path
        d="M8.94 10.548a8.847 8.847 0 0 1 8.838 8.838.825.825 0 0 1-1.406.582.825.825 0 0 1-.241-.582 7.2 7.2 0 0 0-7.191-7.19 7.2 7.2 0 0 0-7.19 7.191.825.825 0 0 1-1.65 0 8.847 8.847 0 0 1 8.84-8.838v-.001ZM8.94 0a5.274 5.274 0 0 1 5.268 5.27 5.274 5.274 0 0 1-5.268 5.268 5.274 5.274 0 0 1-5.268-5.269A5.274 5.274 0 0 1 8.94 0Zm0 1.649a3.625 3.625 0 0 0-3.621 3.62 3.624 3.624 0 0 0 3.62 3.62 3.624 3.624 0 0 0 3.622-3.62A3.626 3.626 0 0 0 8.94 1.65Z"
        fill={color ?? '#207FE7'}
      />
    </Svg>
  );
});
